import React, { useState, useRef, useEffect } from "react";
import {
  GripVertical,
  Plus,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Phone,
  Copy,
  Workflow,
  Sparkles,
  X,
  CircleSlash,
} from "lucide-react";
import FormFieldError from "./FormFieldError";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReactSelect from "react-select";
import {
  BUTTON_LIMITS,
  CHAR_LIMITS,
  CTA_BUTTON_TYPES,
  TC_RADIUS,
} from "../../constants/templateCreatorConstants";
import { COUNTRY_CODES } from "../../constants/countryCodes";

const ICON_MAP = { ExternalLink, Phone, Copy, Workflow };

let btnIdCounter = 1;
const nextId = () => `btn_${Date.now()}_${btnIdCounter++}`;

const INPUT_HEIGHT = 38;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    maxHeight: INPUT_HEIGHT,
    fontSize: 13,
    borderColor: state.isFocused ? "#1877f2" : "#ccd0d5",
    borderRadius: 3,
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: state.isFocused ? "#1877f2" : "#bcc0c4" },
  }),
  valueContainer: (base) => ({
    ...base,
    height: INPUT_HEIGHT - 2,
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#050505" }),
  placeholder: (base) => ({ ...base, color: "#65676b" }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    lineHeight: "18px",
    color: "#050505",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: INPUT_HEIGHT - 2,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#65676b" }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: "8px 10px",
    color: "#050505",
    backgroundColor: state.isSelected
      ? "#e7f3ff"
      : state.isFocused
        ? "#f0f2f5"
        : "white",
    cursor: "pointer",
    ":active": {
      backgroundColor: "#e7f3ff",
      color: "#050505",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, marginBottom: 4 }),
};

const fieldClass = (error = false) =>
  `w-full h-[38px] px-2.5 py-2 text-[13px] border focus:outline-none bg-white ${TC_RADIUS} ${
    error
      ? "border-red-500 focus:border-red-500"
      : "border-[#ccd0d5] focus:border-[#1877f2]"
  }`;

const CharCount = ({ current, max }) => (
  <span className="text-[11px] text-[#65676b] tabular-nums shrink-0 pointer-events-none">
    {current}/{max}
  </span>
);

const FieldLabel = ({ children }) => (
  <label className="text-[12px] text-[#65676b] block mb-1">{children}</label>
);

const RemoveButton = ({ onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`p-1.5 text-[#65676b] hover:text-[#050505] hover:bg-[#f0f2f5] transition-colors mt-6 shrink-0 ${className}`}
    aria-label="Remove"
  >
    <X size={16} strokeWidth={2} />
  </button>
);

const InputWithCount = ({
  value,
  onChange,
  placeholder,
  maxLength,
  error = false,
  errorIcon = false,
  className = "",
}) => (
  <div className="relative">
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`${fieldClass(error)} pl-2.5 ${errorIcon ? "pr-16" : "pr-12"} ${className}`}
    />
    <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
      {errorIcon && (
        <CircleSlash size={14} className="text-red-500 shrink-0" />
      )}
      <CharCount current={(value || "").length} max={maxLength} />
    </span>
  </div>
);

const SelectField = ({ value, onChange, children, disabled = false }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    className={`${fieldClass()} h-[38px] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2365676b%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat pr-8 disabled:bg-[#f0f2f5] disabled:text-[#65676b]`}
  >
    {children}
  </select>
);

const DropdownItem = ({
  icon: Icon,
  label,
  subtitle,
  disabled,
  used,
  limit,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[#f0f2f5] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors border-b border-[#dddfe2] last:border-b-0"
  >
    <Icon
      size={16}
      className={disabled ? "text-[#bcc0c4]" : "text-[#65676b]"}
    />
    <div className="flex-1 min-w-0">
      <p
        className={`text-[13px] ${disabled ? "text-[#bcc0c4]" : "text-[#050505]"}`}
      >
        {label}
      </p>
      <p className="text-[11px] text-[#65676b]">
        {used >= limit ? `${used}/${limit} used` : subtitle}
      </p>
    </div>
    {used > 0 && (
      <span
        className={`text-[10px] tabular-nums px-1.5 py-0.5 ${used >= limit ? "bg-red-50 text-red-500" : "bg-[#f0f2f5] text-[#65676b]"}`}
      >
        {used}/{limit}
      </span>
    )}
  </button>
);

const SortableRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div
        className={`flex items-start gap-2 p-3 border border-[#ccd0d5] bg-white ${TC_RADIUS}`}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="p-1 mt-5 text-[#bcc0c4] hover:text-[#65676b] cursor-grab active:cursor-grabbing shrink-0"
        >
          <GripVertical size={16} />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

const QuickReplyRow = ({ children }) => (
  <div
    className={`mb-2 flex items-start gap-2 p-3 border border-[#ccd0d5] bg-white ${TC_RADIUS}`}
  >
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const ButtonsBuilder = ({
  ctaButtons,
  quickReplyButtons,
  onCtaChange,
  onQrChange,
  flows,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const total = ctaButtons.length + quickReplyButtons.length;
  const counts = {};
  ctaButtons.forEach((b) => {
    counts[b.type] = (counts[b.type] || 0) + 1;
  });

  const getLimit = (type) => BUTTON_LIMITS[type] ?? 1;
  const getUsed = (type) => counts[type] || 0;

  const canAdd = (type) => {
    if (total >= BUTTON_LIMITS.TOTAL) return false;
    if (type === "QUICK_REPLY")
      return quickReplyButtons.length < BUTTON_LIMITS.QUICK_REPLY;
    return getUsed(type) < getLimit(type);
  };

  const canSwitchTo = (currentType, targetType) => {
    if (targetType === currentType) return true;
    const countsAfterRemove = { ...counts };
    countsAfterRemove[currentType] = Math.max(
      0,
      (countsAfterRemove[currentType] || 0) - 1
    );
    return (countsAfterRemove[targetType] || 0) < getLimit(targetType);
  };

  const limitLabel = (type) => {
    const limit = getLimit(type);
    const used = getUsed(type);
    if (limit === 1) return used >= limit ? "1/1 used" : "Max 1";
    return `${used}/${limit} used`;
  };

  const addButton = (type) => {
    setDropdownOpen(false);
    if (type === "QUICK_REPLY") {
      onQrChange([...quickReplyButtons, { id: nextId(), text: "" }]);
      return;
    }
    const base = {
      id: nextId(),
      type,
      text: type === "COPY_OFFER_CODE" ? "Copy offer code" : "",
    };
    if (type === "URL") {
      base.url = "";
      base.url_type = "static";
      base.urlVariableSample = "";
    } else if (type === "PHONE_NUMBER") {
      base.country_code = "+1";
      base.phone_number = "";
    } else if (type === "COPY_OFFER_CODE") {
      base.offer_code = "";
    } else if (type === "FLOW") {
      base.flow_id = "";
    }
    onCtaChange([...ctaButtons, base]);
  };

  const updateCta = (id, field, value) => {
    onCtaChange(
      ctaButtons.map((b) => (b.id === id ? { ...b, [field]: value } : b))
    );
  };

  const updateQr = (id, field, value) => {
    onQrChange(
      quickReplyButtons.map((b) =>
        b.id === id ? { ...b, [field]: value } : b
      )
    );
  };

  const removeCta = (id) =>
    onCtaChange(ctaButtons.filter((b) => b.id !== id));
  const removeQr = (id) =>
    onQrChange(quickReplyButtons.filter((b) => b.id !== id));

  const changeCtaType = (id, newType) => {
    onCtaChange(
      ctaButtons.map((b) => {
        if (b.id !== id) return b;
        const base = {
          id,
          type: newType,
          text:
            newType === "COPY_OFFER_CODE" ? "Copy offer code" : b.text,
        };
        if (newType === "URL") {
          base.url = "";
          base.url_type = "static";
          base.urlVariableSample = "";
        } else if (newType === "PHONE_NUMBER") {
          base.country_code = "+1";
          base.phone_number = "";
        } else if (newType === "COPY_OFFER_CODE") {
          base.offer_code = "";
        } else if (newType === "FLOW") {
          base.flow_id = "";
        }
        return base;
      })
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleCtaDrag = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = ctaButtons.map((b) => b.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx !== -1 && newIdx !== -1)
      onCtaChange(arrayMove(ctaButtons, oldIdx, newIdx));
  };

  const countryOptions = COUNTRY_CODES.map((c) => ({
    value: c.dialCode,
    label: `${c.code} ${c.dialCode}`,
  }));

  const renderCtaFields = (btn) => {
    const phoneEmpty =
      btn.type === "PHONE_NUMBER" &&
      btn.phone_number !== undefined &&
      !btn.phone_number?.trim();
    const offerEmpty =
      btn.type === "COPY_OFFER_CODE" &&
      btn.offer_code !== undefined &&
      !btn.offer_code?.trim();

    return (
      <div>
        <div className="flex items-start gap-2">
          <div className="flex flex-1 min-w-0 items-start gap-2 flex-wrap xl:flex-nowrap">
            <div className="w-[148px] shrink-0">
              <FieldLabel>Type of action</FieldLabel>
              <SelectField
                value={btn.type}
                onChange={(e) => {
                  if (canSwitchTo(btn.type, e.target.value))
                    changeCtaType(btn.id, e.target.value);
                }}
              >
                {CTA_BUTTON_TYPES.map((t) => {
                  const allowed = canSwitchTo(btn.type, t.value);
                  return (
                    <option
                      key={t.value}
                      value={t.value}
                      disabled={!allowed}
                    >
                      {t.label}
                      {!allowed ? ` (${limitLabel(t.value)})` : ""}
                    </option>
                  );
                })}
              </SelectField>
            </div>

            <div className="flex-1 min-w-[120px]">
              <FieldLabel>Button text</FieldLabel>
              <InputWithCount
                value={btn.text}
                onChange={(e) => updateCta(btn.id, "text", e.target.value)}
                placeholder="Button text"
                maxLength={CHAR_LIMITS.BUTTON_TEXT}
              />
            </div>

            {btn.type === "URL" && (
              <>
                <div className="w-[96px] shrink-0">
                  <FieldLabel>URL type</FieldLabel>
                  <SelectField
                    value={btn.url_type || "static"}
                    onChange={(e) =>
                      updateCta(btn.id, "url_type", e.target.value)
                    }
                  >
                    <option value="static">Static</option>
                    <option value="dynamic">Dynamic</option>
                  </SelectField>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <FieldLabel>Website URL</FieldLabel>
                  <InputWithCount
                    value={btn.url || ""}
                    onChange={(e) => updateCta(btn.id, "url", e.target.value)}
                    placeholder={
                      btn.url_type === "dynamic"
                        ? "https://example.com/{{1}}"
                        : "https://example.com"
                    }
                    maxLength={CHAR_LIMITS.WEBSITE_URL}
                  />
                </div>
              </>
            )}

            {btn.type === "PHONE_NUMBER" && (
              <>
                <div className="w-[108px] shrink-0">
                  <FieldLabel>Country</FieldLabel>
                  <ReactSelect
                    options={countryOptions}
                    value={
                      countryOptions.find(
                        (o) => o.value === btn.country_code
                      ) || countryOptions[0]
                    }
                    onChange={(opt) =>
                      updateCta(btn.id, "country_code", opt?.value || "+1")
                    }
                    menuPortalTarget={document.body}
                    menuPlacement="top"
                    menuPosition="fixed"
                    maxMenuHeight={220}
                    styles={selectStyles}
                    isClearable={false}
                    isSearchable
                  />
                  <FormFieldError className="min-h-[36px]" />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <FieldLabel>Phone number</FieldLabel>
                  <InputWithCount
                    value={btn.phone_number || ""}
                    onChange={(e) =>
                      updateCta(btn.id, "phone_number", e.target.value)
                    }
                    placeholder="5551234567"
                    maxLength={CHAR_LIMITS.PHONE_NUMBER}
                    error={phoneEmpty}
                    errorIcon={phoneEmpty}
                  />
                  <FormFieldError
                    message={
                      phoneEmpty
                        ? "You need to enter a phone number. Please add a valid phone number."
                        : null
                    }
                    className="min-h-[36px]"
                  />
                </div>
              </>
            )}

            {btn.type === "COPY_OFFER_CODE" && (
              <div className="flex-1 min-w-[120px]">
                <FieldLabel>Offer code</FieldLabel>
                <InputWithCount
                  value={btn.offer_code || ""}
                  onChange={(e) =>
                    updateCta(btn.id, "offer_code", e.target.value)
                  }
                  placeholder="Add sample text"
                  maxLength={CHAR_LIMITS.OFFER_CODE}
                  error={offerEmpty}
                />
                <FormFieldError
                  message={offerEmpty ? "Add sample text" : null}
                />
              </div>
            )}

            {btn.type === "FLOW" && (
              <div className="flex-1 min-w-[120px]">
                <FieldLabel>Flow</FieldLabel>
                <SelectField
                  value={btn.flow_id || ""}
                  onChange={(e) =>
                    updateCta(btn.id, "flow_id", e.target.value)
                  }
                >
                  <option value="">Select a flow</option>
                  {(flows || []).map((f) => (
                    <option key={f.id || f._id} value={f.id || f._id}>
                      {f.name || f.flowName || `Flow ${f.id || f._id}`}
                    </option>
                  ))}
                </SelectField>
              </div>
            )}
          </div>

          <RemoveButton onClick={() => removeCta(btn.id)} />
        </div>

        {btn.type === "URL" && btn.url_type === "dynamic" && (
          <div
            className={`mt-2 flex items-center gap-2 bg-[#f0f2f5] p-2 border border-[#dddfe2] ${TC_RADIUS}`}
          >
            <span
              className={`text-[11px] font-mono text-[#65676b] bg-white px-1.5 py-0.5 border border-[#ccd0d5] shrink-0 ${TC_RADIUS}`}
            >
              {"{{1}}"}
            </span>
            <input
              type="text"
              value={btn.urlVariableSample || ""}
              onChange={(e) =>
                updateCta(btn.id, "urlVariableSample", e.target.value)
              }
              placeholder="Sample variable value, e.g. ORDER_12345"
              className={`flex-1 px-2.5 py-1.5 text-[13px] border border-[#ccd0d5] focus:outline-none focus:border-[#1877f2] bg-white ${TC_RADIUS}`}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-[15px] font-semibold text-[#050505] mb-1">
        Buttons · Optional
      </h3>
      <p className="text-[13px] text-[#65676b] mb-3 leading-relaxed">
        Create buttons that let customers respond to your message or take
        action. You can add up to ten buttons. If you add more than three
        buttons, they will appear in a list.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={total >= BUTTON_LIMITS.TOTAL}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold text-[#050505] bg-white border border-[#ccd0d5] hover:bg-[#f0f2f5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${TC_RADIUS}`}
          >
            <Plus size={14} />
            Add button
            {dropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {dropdownOpen && (
            <div
              className={`absolute bottom-full left-0 mb-1 w-60 bg-white border border-[#ccd0d5] shadow-lg z-[60] py-0.5 ${TC_RADIUS}`}
            >
              <DropdownItem
                icon={Sparkles}
                label="Custom"
                subtitle={`Max ${BUTTON_LIMITS.QUICK_REPLY}`}
                disabled={!canAdd("QUICK_REPLY")}
                used={quickReplyButtons.length}
                limit={BUTTON_LIMITS.QUICK_REPLY}
                onClick={() => addButton("QUICK_REPLY")}
              />
              {CTA_BUTTON_TYPES.map((t) => {
                const Icon = ICON_MAP[t.icon] || ExternalLink;
                const limit = getLimit(t.value);
                const used = getUsed(t.value);
                return (
                  <DropdownItem
                    key={t.value}
                    icon={Icon}
                    label={t.label}
                    subtitle={`Max ${limit}`}
                    disabled={!canAdd(t.value)}
                    used={used}
                    limit={limit}
                    onClick={() => addButton(t.value)}
                  />
                );
              })}
            </div>
          )}
        </div>
        <span className="text-[12px] text-[#65676b] tabular-nums">
          {total}/{BUTTON_LIMITS.TOTAL} buttons
        </span>
      </div>

      {quickReplyButtons.length > 0 && (
        <div className="mb-4">
          <p className="text-[13px] font-semibold text-[#65676b] mb-2">
            Quick reply · Optional
          </p>
          {quickReplyButtons.map((btn) => (
            <QuickReplyRow key={btn.id}>
              <div className="flex items-start gap-2">
                <div className="w-[120px] shrink-0">
                  <FieldLabel>Type</FieldLabel>
                  <SelectField value="custom" disabled onChange={() => {}}>
                    <option value="custom">Custom</option>
                  </SelectField>
                </div>
                <div className="flex-1 min-w-0">
                  <FieldLabel>Button text</FieldLabel>
                  <InputWithCount
                    value={btn.text}
                    onChange={(e) => updateQr(btn.id, "text", e.target.value)}
                    placeholder="Quick Reply"
                    maxLength={CHAR_LIMITS.BUTTON_TEXT}
                  />
                </div>
                <RemoveButton onClick={() => removeQr(btn.id)} />
              </div>
            </QuickReplyRow>
          ))}
        </div>
      )}

      {ctaButtons.length > 0 && (
        <div className="mb-4">
          <p className="text-[13px] font-semibold text-[#65676b] mb-2">
            Call to action · Optional
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCtaDrag}
          >
            <SortableContext
              items={ctaButtons.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              {ctaButtons.map((btn) => (
                <SortableRow key={btn.id} id={btn.id}>
                  {renderCtaFields(btn)}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default ButtonsBuilder;
